<template>
  <div class="dpv" data-cy="pdf-viewer">
    <!-- toolbar -->
    <div class="dpv-toolbar">
      <button class="dpv-tool-btn" :disabled="!ready" aria-label="Zoom out" @click="zoomOut">−</button>
      <span class="dpv-zoom">{{ zoom }}%</span>
      <button class="dpv-tool-btn" :disabled="!ready" aria-label="Zoom in" @click="zoomIn">+</button>
      <button class="dpv-tool-btn dpv-fit" :disabled="!ready" @click="fitWidth">Fit width</button>
      <button class="dpv-tool-btn" :disabled="!ready" aria-label="Rotate" @click="rotate">⤾</button>
      <span class="dpv-spacer"></span>
      <span v-if="isPdf && ready" class="dpv-pageinfo">Page {{ page }} / {{ numPages }}</span>
    </div>

    <div class="dpv-main">
      <!-- thumbnail rail (multi-page PDFs, desktop) -->
      <div v-if="isPdf && ready && numPages > 1" class="dpv-thumbs">
        <button
          v-for="n in numPages"
          :key="n"
          class="dpv-thumb"
          :class="{ active: n === page }"
          @click="page = n"
        >
          {{ n }}
        </button>
      </div>

      <!-- page area -->
      <div class="dpv-scroll">
        <!-- loading skeleton -->
        <div v-if="loading" class="dpv-skeleton" :style="{ width: zoom + '%' }"></div>

        <!-- error -->
        <div v-else-if="error" class="dpv-error">
          <div class="dpv-error-icon">⚠</div>
          <div class="dpv-error-title">{{ url ? "Preview unavailable" : "Document unavailable" }}</div>
          <div v-if="url" class="dpv-error-sub">This document couldn't be rendered for preview.</div>
          <a v-if="url" :href="url" target="_blank" rel="noopener" class="dpv-error-link">
            <span class="glyphicon glyphicon-new-window bi bi-box-arrow-up-right"></span>
            Open in new tab
          </a>
        </div>

        <!-- image document -->
        <img
          v-else-if="isImage && imageSrc"
          :src="imageSrc"
          :alt="title || 'Document'"
          class="dpv-img"
          :style="{ width: zoom + '%', transform: 'rotate(' + rotation + 'deg)' }"
          @error="error = true"
        />

        <!-- pdf page canvas -->
        <div
          v-else
          ref="canvasHost"
          class="dpv-canvas-host"
          :style="{ width: zoom + '%' }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Document pane for the code assigner: renders PDF pages to canvas via the
// lazily-imported pdfjs wrapper (utils/pdfRender.js) — real page count,
// thumbnails, zoom and rotate — and falls back to <img> for camera uploads.
// Never blocks its parent: failures show a non-blocking "Preview unavailable"
// state with an open-in-new-tab escape hatch.
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { isImageContentType } from "@/utils/directiveDocument";
// `url` is a file reference; an <img> loads a single-use content ticket URL
// resolved from it (PDF.js resolves its own in pdfRender), because neither can
// send the bearer token.
import { resolveFileUrl } from "@/api2/fileTickets";

const props = defineProps({
  url: { type: String, default: "" },
  title: { type: String, default: "" },
  // The document row's content type; the URL (file.php?id=) carries no extension.
  contentType: { type: String, default: "" },
});

const isImage = computed(() => isImageContentType(props.contentType));
const isPdf = computed(() => !isImage.value);

const loading = ref(true);
const error = ref(false);
const ready = computed(() => !loading.value && !error.value);

const page = ref(1);
const numPages = ref(1);
const zoom = ref(100); // % of the pane width (50–150, steps of 25)
const rotation = ref(0);

const canvasHost = ref(null);
let doc = null; // pdfRender handle
const imageSrc = ref(""); // the ticket URL an image document loads from
let imageToken = 0;
let renderToken = 0;

function zoomIn() {
  zoom.value = Math.min(150, zoom.value + 25);
}
function zoomOut() {
  zoom.value = Math.max(50, zoom.value - 25);
}
function fitWidth() {
  zoom.value = 100;
}
function rotate() {
  rotation.value = (rotation.value + 90) % 360;
}

async function loadDocument() {
  loading.value = true;
  error.value = false;
  page.value = 1;
  rotation.value = 0;
  if (doc) {
    doc.destroy();
    doc = null;
  }
  if (!props.url) {
    loading.value = false;
    error.value = true;
    return;
  }
  if (isImage.value) {
    // <img> handles its own load; errors flip the flag via @error. A load
    // that a newer one overtook leaves the newer one's source alone.
    const token = ++imageToken;
    imageSrc.value = "";
    const resolved = await resolveFileUrl(props.url);
    if (token !== imageToken) return;
    loading.value = false;
    if (resolved.ok) {
      imageSrc.value = resolved.url;
    } else {
      error.value = true;
    }
    return;
  }
  try {
    const { openPdf } = await import("@/utils/pdfRender");
    doc = await openPdf(props.url);
    numPages.value = doc.numPages;
    loading.value = false;
    await renderCurrentPage();
  } catch (e) {
    console.error("[directives] pdf preview failed", e);
    loading.value = false;
    error.value = true;
  }
}

async function renderCurrentPage() {
  if (!doc || !isPdf.value) return;
  const token = ++renderToken;
  await nextTick();
  const host = canvasHost.value;
  if (!host) return;
  // Render at the host's current CSS width so zoom changes re-render sharply.
  const width = Math.max(host.clientWidth, 240);
  try {
    const canvas = await doc.renderPage(page.value, { width, rotation: rotation.value });
    if (token !== renderToken || !canvasHost.value) return; // stale render
    canvasHost.value.replaceChildren(canvas);
  } catch (e) {
    if (token !== renderToken) return;
    console.error("[directives] pdf page render failed", e);
    error.value = true;
  }
}

// The type decides image vs PDF, so a change to either reloads.
watch(() => [props.url, props.contentType], loadDocument);
watch([page, zoom, rotation], renderCurrentPage);

onMounted(loadDocument);
onBeforeUnmount(() => {
  renderToken++;
  if (doc) doc.destroy();
  doc = null;
});
</script>

<style scoped>
.dpv {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--eh-muted-fill);
}
.dpv-toolbar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: var(--eh-white);
  border-bottom: 1px solid var(--eh-divider);
}
.dpv-tool-btn {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border-radius: 7px;
  border: 1px solid var(--eh-divider);
  background: var(--eh-white);
  color: var(--eh-muted);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.dpv-tool-btn:hover:not(:disabled) {
  background: var(--eh-muted-fill);
}
.dpv-tool-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.dpv-fit {
  font-size: 12px;
}
.dpv-zoom {
  font-size: 12.5px;
  color: var(--eh-muted);
  min-width: 40px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.dpv-spacer {
  flex: 1;
}
.dpv-pageinfo {
  font-size: 12.5px;
  color: var(--eh-muted);
}
.dpv-main {
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
}
.dpv-thumbs {
  width: 56px;
  flex: none;
  border-right: 1px solid var(--eh-divider);
  background: var(--eh-white);
  overflow-y: auto;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dpv-thumb {
  height: 44px;
  border-radius: 6px;
  border: 1px solid var(--eh-divider);
  background: var(--eh-muted-fill);
  color: var(--eh-muted);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.dpv-thumb.active {
  border-color: var(--eh-teal);
  background: var(--eh-teal-wash);
  color: var(--eh-teal);
}
.dpv-scroll {
  flex: 1;
  overflow: auto;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
.dpv-skeleton {
  max-width: 640px;
  aspect-ratio: 8.5 / 11;
  border-radius: 6px;
  border: 1px solid var(--eh-divider);
  background: linear-gradient(100deg, var(--eh-muted-fill) 30%, var(--eh-divider) 50%, var(--eh-muted-fill) 70%);
  background-size: 200% 100%;
  animation: dpvShimmer 1.3s ease infinite;
  flex: none;
}
@keyframes dpvShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.dpv-error {
  align-self: center;
  width: 100%;
  max-width: 400px;
  text-align: center;
  padding: 38px 24px;
  background: var(--eh-white);
  border: 1px solid var(--eh-divider);
  border-radius: 12px;
}
.dpv-error-icon {
  font-size: 26px;
  color: var(--eh-muted-border);
  margin-bottom: 10px;
}
.dpv-error-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--eh-teal-ink);
  margin-bottom: 6px;
}
.dpv-error-sub {
  font-size: 12.5px;
  color: var(--eh-muted);
  margin-bottom: 16px;
}
.dpv-error-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid var(--eh-divider);
  background: var(--eh-white);
  color: var(--eh-teal);
  text-decoration: none;
  font-size: 12.5px;
  font-weight: 600;
}
.dpv-error-link:hover {
  background: var(--eh-teal-wash);
}
.dpv-img {
  max-width: none;
  flex: none;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(26, 54, 93, 0.12);
  transition: transform 0.2s;
}
.dpv-canvas-host {
  flex: none;
}
/* canvas is appended by JS (no scope attr), so :deep() is required */
.dpv-canvas-host :deep(canvas) {
  width: 100% !important;
  height: auto !important;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(26, 54, 93, 0.12);
  background: var(--eh-white);
}
</style>
